import * as THREE from "three";
import gsap from "gsap";
import useFlowStore from "../store/flowStore";

let cameraManager = {
  scene: null,
  animations: null,
  mixer: null,
  set: null,
};

let currentCamera = null;
let baseLookTarget = new THREE.Vector3();       // Zone autour du lookAt
let currentLookTarget = new THREE.Vector3();    // LookAt interpolé
let initialLookTarget = new THREE.Vector3();    // LookAt d'origine (figé)
let isControlling = false;
let mouse = { x: 0, y: 0 };
let gsapLookTween = null;

// 🔍 Calcule la cible actuelle de regard de la caméra
function getCameraLookAt(camera) {
  const dir = new THREE.Vector3();
  camera.getWorldDirection(dir);
  return camera.position.clone().add(dir.multiplyScalar(10));
}


// 👁️ Réagit aux mouvements de souris pour déplacer doucement la cible
function onMouseMove(event) {
  if (!isControlling || !currentCamera) return;

  // Utilise la liberté stockée, sinon valeur par défaut
  const maxOffset = enableMouseLook._freedom || 1.75;

  mouse.x = (event.clientX / window.innerWidth - 0.5) * 2;
  mouse.y = (event.clientY / window.innerHeight - 0.5) * 2;

  const offsetX = THREE.MathUtils.clamp(mouse.x, -1, 1) * maxOffset;
  const offsetY = THREE.MathUtils.clamp(mouse.y, -1, 1) * maxOffset;

  const target = baseLookTarget.clone().add(new THREE.Vector3(offsetX, -offsetY, 0));

  if (gsapLookTween) gsapLookTween.kill();

  gsapLookTween = gsap.to(currentLookTarget, {
    x: target.x,
    y: target.y,
    z: target.z,
    duration: 5,
    ease: "expo.out",
    onUpdate: () => {
      currentCamera.lookAt(currentLookTarget);
    }
  });
}


// ✅ Active le contrôle de regard avec transition douce
function enableMouseLook(duration = 1, freedom = 1.75) {
  if (!currentCamera) {
    console.warn("📸 Aucun currentCamera actif pour enableMouseLook");
    return;
  }

  // On stocke la liberté de mouvement dans une variable globale ou locale
  enableMouseLook._freedom = freedom;

  const lookAtNow = getCameraLookAt(currentCamera);

  baseLookTarget.copy(lookAtNow);
  initialLookTarget.copy(lookAtNow);
  currentLookTarget.copy(lookAtNow);

  gsap.to({}, {
    duration,
    ease: "power2.inOut",
    onComplete: () => {
      isControlling = true;
      window.addEventListener("mousemove", onMouseMove);
    }
  });
}

// ❌ Désactive proprement le contrôle de la souris
function disableMouseLook() {
  isControlling = false;
  window.removeEventListener("mousemove", onMouseMove);
  if (gsapLookTween) gsapLookTween.kill();
}

// 🔁 Reset fluide du lookAt sans lancer d'animation
function resetLookTarget(duration = 1) {
  if (!currentCamera) {
    console.warn("📸 Aucun currentCamera actif pour resetLookTarget");
    return;
  }

  disableMouseLook();
  baseLookTarget.copy(initialLookTarget);

  if (gsapLookTween) gsapLookTween.kill();

  gsapLookTween = gsap.to(currentLookTarget, {
    x: initialLookTarget.x,
    y: initialLookTarget.y,
    z: initialLookTarget.z,
    duration,
    ease: "expo.out",
    onUpdate: () => {
      currentCamera.lookAt(currentLookTarget);
    }
  });
}

// 🧠 Reset puis enchaînement d'animation (sans saut)
function resetLookTargetWithAnimation({ duration = 1, onComplete } = {}) {
  if (!currentCamera) {
    console.warn("📸 Aucun currentCamera actif pour resetLookTargetWithAnimation");
    return;
  }

  disableMouseLook();
  baseLookTarget.copy(initialLookTarget);

  if (gsapLookTween) gsapLookTween.kill();

  gsapLookTween = gsap.to(currentLookTarget, {
    x: initialLookTarget.x,
    y: initialLookTarget.y,
    z: initialLookTarget.z,
    duration,
    ease: "expo.out",
    onUpdate: () => {
      currentCamera.lookAt(currentLookTarget);
    },
    onComplete: () => {
      if (typeof onComplete === "function") onComplete();
    }
  });
}

const CameraManager = {
  register({ scene, animations, set, onReady }) {
    cameraManager.scene = scene;
    cameraManager.animations = animations;
    cameraManager.set = set;
    cameraManager.mixer = animations.mixer;

    Object.values(animations.actions).forEach(action => {
      action.reset();
      action.paused = true;
      action.enabled = true;
      action.setEffectiveTimeScale(0);
      action.setEffectiveWeight(1);
      action.play();
      action.time = 0;
    });

    cameraManager.mixer.update(0);

    console.log("✅ CameraManager registered !");
    if (typeof onReady === "function") onReady(); // 👈 appelle le callback

  },

  reset() {
    const { scene, animations } = cameraManager;
    if (scene && animations) {
      Object.values(animations.actions).forEach(action => {
        action.stop();
        action.reset();
        action.paused = true;
        action.setEffectiveTimeScale(0);
        action.setEffectiveWeight(1);
        action.play();
        action.time = 0;
      });
      cameraManager.mixer.update(0);
    }
  },

  isReady() {
    return !!(cameraManager.scene && cameraManager.animations && cameraManager.set);
  },

  getCurrentCamera() {
    return currentCamera;
  },

  switchCamera(cameraName, options = {}) {
    const { scene, set } = cameraManager;
    const camera = scene?.getObjectByName(cameraName);
  
    if (!camera) {
      console.warn(`Camera '${cameraName}' not found.`);
      return;
    }
  
    // Utilise les options, sinon valeurs par défaut
    camera.near = options.near !== undefined ? options.near : 0.1;
    camera.far = options.far !== undefined ? options.far : 1000;
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  
    set({ camera });
    currentCamera = camera;
  
    console.log("✅ Camera activée :", camera.name, camera.position.toArray());
    console.trace("🔍 cameraManager", cameraManager);
  },
  

  playAnimation(onFinished, maxDuration = null) {
    const { animations, mixer } = cameraManager;
    // console.log("🔍 animations", animations); 
    let action;
    if (animations.actions.Anim_0) {
      action = animations?.actions?.Anim_0;
    } else {
      action = animations?.actions?.animation_0;
    }

    if (action) {
      action.stop();
      action.reset();
      action.paused = false;
      action.setEffectiveTimeScale(1);
      action.setLoop(THREE.LoopOnce, 1);
      action.clampWhenFinished = true;

      let timeoutId;

      if (onFinished && mixer) {
        const finishedHandler = (e) => {
          if (e.action === action) {
            if (timeoutId) clearTimeout(timeoutId);
            mixer.removeEventListener('finished', finishedHandler);
            onFinished();
          }
        };
        mixer.addEventListener('finished', finishedHandler);
      }

      if (maxDuration !== null) {
        timeoutId = setTimeout(() => {
          console.warn("Animation forcée à l'arrêt après délai.");
          // PAS de stop(), PAS de reset() !
          // PAS de changement d'action.time ici !
          onFinished?.();
        }, maxDuration);
      }
      
      action.play();
    } else {
      console.warn("CameraManager: No animation_0 action found to play.");
    }
  },

  playAnimationFrom({ name = "animation_0", startTime = 0, onFinished, maxDuration = null }) {
    const { animations, mixer } = cameraManager;
    const action = animations?.actions?.[name];

    if (!action) {
      console.warn(`CameraManager: Animation '${name}' not found.`);
      return;
    }

    action.stop();
    action.reset();
    action.paused = false;
    action.setEffectiveTimeScale(1);
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.time = startTime;

    let timeoutId;

    if (onFinished && mixer) {
      const finishedHandler = (e) => {
        if (e.action === action) {
          if (timeoutId) clearTimeout(timeoutId);
          mixer.removeEventListener('finished', finishedHandler);
          onFinished();
        }
      };
      mixer.addEventListener('finished', finishedHandler);
    }

    if (maxDuration !== null) {
      timeoutId = setTimeout(() => {
        console.warn("Animation forcée à l'arrêt après délai.");
        action.stop();
        action.reset();
        onFinished?.();
      }, maxDuration);
    }

    action.play();
  },

  async switchAndPlay(cameraName, onFinished) {
    if (!this.isReady()) {
      console.warn("CameraManager not ready yet, delaying switch...");
      await new Promise(resolve => setTimeout(resolve, 100));
      return this.switchAndPlay(cameraName, onFinished);
    }

    this.switchCamera(cameraName);

    this.playAnimation(() => {
      this.enableMouseLook(1);
      onFinished?.();
    });
  },

  // ✅ API publique
  enableMouseLook,
  disableMouseLook,
  resetLookTarget,
  resetLookTargetWithAnimation,
  setLookTarget(target) {
    baseLookTarget.copy(target);
    currentLookTarget.copy(target);
  }
};

export default CameraManager;
